import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import mapboxgl from 'mapbox-gl';

import {
  FilterMethod,
  MapFilterType
} from '@core/models/data-visualization/data-visualization.state';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import {
  setActionFocus,
  setFilterOnAggregator
} from '@core/store/data-visualization/data-visualization.actions';
import { selectAggregatorFilter } from '@core/store/data-visualization/data-visualization.selectors';
import { AutoUnsubscribe } from '@core/utils/auto-unsubscribe';
import { dedup } from '@core/utils/shared-utils';

import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { HighlightAggregatorMarker } from '@features/components/visualization/mapbox/models/map-data';
import { MapRegistry } from '@features/components/visualization/mapbox/services/registry.service';

@Injectable({
  providedIn: 'root'
})
export class MapFilterService extends AutoUnsubscribe {
  map!: mapboxgl.Map;
  markers: GaliaMarker[] = [];
  isGroupingEnabled = true;
  readonly isCurrentlyFiltering = () => this.isFiltered;
  changeGroupingFonction: ((grouping: boolean, markers: GaliaMarker[]) => void) | undefined;
  private isFilterServiceActive = false;
  private isFiltered = false;
  private registry: MapRegistry | undefined;
  private store = inject(Store);
  private focus = inject(VisualFocusService);
  private selectedStructuralAggregatorId$ = this.store.select(selectAggregatorFilter);
  private aggregatorFilter = this.store.selectSignal(selectAggregatorFilter);

  constructor() {
    super();

    const sub = this.selectedStructuralAggregatorId$.subscribe((filter) => {
      if (!this.isFilterServiceActive || !this.map || !filter) {
        return;
      }

      const ids: string[] = filter.filteredAggregatorIds;
      if (ids && ids.length > 0 && ids[0] !== MapFilterType.RESET) {
        this.isFiltered = true;

        if (filter.filterMethod === FilterMethod.BY_AGGREGATOR) {
          this.filterMarkersAndRoutesByStructuralAggregator(ids);
        }

        if (filter.filterMethod === FilterMethod.BY_HIGHLIGHT_MARKER) {
          this.filterByHighlightAggregatorIds({
            aggregatorIds: ids,
            markerSelectedId: filter.markerId ?? MapFilterType.RESET
          });
        }
      } else {
        if (!this.isFiltered) {
          return;
        }
        this.isFiltered = false;
        this.resetFilters();
      }
    });

    this.subscriptionsManager.add(sub);
  }

  activateFilterService(map: mapboxgl.Map) {
    this.isFilterServiceActive = true;
    if (this.map !== map) {
      this.map = map;
      this.registerGlobalMapEvents();
    }
  }

  disableFilterService() {
    this.isFilterServiceActive = false;
    // Only when there is something to undo. `resetFilters` dispatches the DISABLED reset,
    // which the reducer reads as clearing the job pin too, so an unconditional call here
    // would make tearing the map down drop a pin the timeline is still showing.
    if (!this.isFiltered) return;
    this.isFiltered = false;
    this.resetFilters();
  }

  filterByHighlightAggregatorIds(highlightAggregator: HighlightAggregatorMarker) {
    if (!this.registry) return;

    const filter: mapboxgl.FilterSpecification[] = (
      highlightAggregator.aggregatorIds as string[]
    ).map((id) => {
      return ['in', id.toString(), ['get', 'highlightAggregatorIds']];
    });

    this.filterMarkers(['any', ...filter], false);
    this.map.setPaintProperty(this.registry.markers.layers.selected, 'icon-opacity', [
      'match',
      ['get', 'id'],
      highlightAggregator.markerSelectedId,
      1,
      0.5
    ]);
    this.filterPathsViewBasedOnMarkerIds([highlightAggregator.markerSelectedId]);
  }

  filterMarkersAndRoutesByStructuralAggregator(ids: string[] | null | undefined) {
    if (!ids || ids.length === 0) {
      return;
    }

    // The ids name *lines* — a click on the map carries the uid of the line it landed on — and a
    // line may belong to an entity instead of being one: a job draws an arc per option and names
    // each arc after itself, while the job's own pickup and delivery markers name the job. The
    // stops are matched against both, or a job's filter matches no marker at all and every one
    // of its operations is drawn as a grey dot by `unselected`.
    //
    // The lines keep the ids exactly as dispatched: their features are keyed by the arc's own
    // uid, so filtering them by the entity would take the arc off the map.
    const markerIds = dedup(ids.flatMap((id) => [id, this.focus.aggregatorOfRoute(id)]));
    const filter: mapboxgl.FilterSpecification[] = markerIds.map((id) => {
      return ['in', id.toString(), ['get', 'structuralAggregatorIds']];
    });

    this.filterMarkers(['any', ...filter]);
    this.filterRoutesBySA(ids);
  }

  registerGlobalMapEvents() {
    this.map.on('click', (event: mapboxgl.MapMouseEvent) => {
      // A click that landed on a route or a stop is what raises the filter or the pin in the
      // first place, so it must never also read as a background click. The cursor alone does
      // not say so: it is cleared by whichever hit layer the pointer leaves first, and a
      // stop's hit circle is wider than its route's hit band, so a pointer resting on a stop
      // just off the line has already had the cursor unset under it. Hit-testing the point is
      // what still recognises the stop — without it, a second click on a pinned stop clears
      // the pin here and the marker handler immediately raises it again.
      if (this.map.getCanvas().style.cursor === 'pointer') return;
      if (this.hitsInteractiveLayer(event.point)) return;

      if (this.isFiltered) {
        this.resetFilters();
        return;
      }

      // A job pin is raised without any hard filter, so `isFiltered` alone leaves the
      // background click with nothing to clear it. Clearing it on its own rather than through
      // `resetFilters`: that path re-orders nine marker layers and rebuilds the whole marker
      // GeoJSON, and clearing a pin is the common case here — there is no filter to undo.
      if (this.aggregatorFilter().focusedActionUid) {
        this.store.dispatch(setActionFocus({ actionUid: null }));
      }
    });
  }

  /** The DISABLED dispatch is what clears a job pin: the reducer treats it as the reset. */
  resetFilters() {
    this.resetRoutes();
    this.resetMarkers();
    this.store.dispatch(
      setFilterOnAggregator({
        filteredAggregatorIds: [MapFilterType.RESET],
        filterMethod: FilterMethod.DISABLED
      })
    );
  }

  setRegistry(registry: MapRegistry) {
    this.registry = registry;
  }

  /**
   * The layers whose own click handlers claim a click. `queryRenderedFeatures` throws on a
   * layer the style does not hold, so a map carrying none of them is filtered out first.
   */
  private hitsInteractiveLayer(point: mapboxgl.Point): boolean {
    if (!this.registry) return false;

    const markers = this.registry.markers.layers;
    const layers = [
      markers.invisible,
      markers.unclusteredInvisible,
      markers.unclusteredSpiderfy,
      markers.clustered,
      this.registry.routes.layers.invisible
    ].filter((layer) => this.map.getLayer(layer));

    return (
      layers.length > 0 && this.map.queryRenderedFeatures(point, { layers }).length > 0
    );
  }

  private filterMarkers(
    filter: mapboxgl.FilterSpecification,
    filterInvisible: boolean = true
  ) {
    if (!this.registry) return;

    if (this.changeGroupingFonction) {
      this.changeGroupingFonction(false, this.markers);
    }

    this.setFilterIfChanged(this.registry.markers.layers.selected, filter);
    // The two unlit layers partition the stops between them, or a stop this filter excluded
    // would take a dot from each and composite to twice the ink of every other unlit stop.
    // `unselected` owns the excluded side; `dim` is left the side the filter kept, where a
    // focus may yet push some of them away.
    this.setFilterIfChanged(this.registry.markers.layers.dim, filter);
    this.setFilterIfChanged(this.registry.markers.layers.unselected, ['!', filter]);

    if (filterInvisible) {
      this.setFilterIfChanged(this.registry.markers.layers.invisible, filter);
    }

    this.setVisibilityIfChanged(this.registry.markers.layers.unclusteredInvisible, 'none');
    this.setVisibilityIfChanged(this.registry.markers.layers.invisible, 'visible');
    this.setVisibilityIfChanged(this.registry.markers.layers.unselected, 'visible');
    this.setVisibilityIfChanged(this.registry.markers.layers.selected, 'visible');
    this.setVisibilityIfChanged(this.registry.markers.layers.default, 'none');
  }

  private filterPathsViewBasedOnMarkerIds(selectedMarkersIds: string[]) {
    const dataFiltered = this.markers.filter((marker) =>
      selectedMarkersIds.includes(marker.uid)
    );
    const structuralAggregatorIds: string[] = [];
    dataFiltered.forEach((marker) => {
      if (marker.structuralAggregatorIds && marker.structuralAggregatorIds.length > 0) {
        marker.structuralAggregatorIds.forEach((id) => {
          if (!structuralAggregatorIds.includes(id) && id != null) {
            structuralAggregatorIds.push(id);
          }
        });
      }
    });
    this.filterRoutesBySA(structuralAggregatorIds);
  }

  private filterRoutesBySA(ids: string[]) {
    if (!this.registry) return;

    const filter = [
      'in',
      ['get', 'structuralAggregatorIds'],
      ['literal', ids]
    ] as mapboxgl.FilterSpecification;

    this.setFilterIfChanged(this.registry.routes.layers.default, filter);
    // The casing follows the same filter as the line it sits under: a focus outliving a filter
    // change would otherwise draw a white halo where the route itself has been filtered away.
    this.setFilterIfChanged(this.registry.routes.layers.casing, filter);
    this.setFilterIfChanged(this.registry.routes.layers.dashed, filter);
    this.setFilterIfChanged(this.registry.routes.layers.symbols, filter);
    this.setFilterIfChanged(this.registry.routes.layers.invisible, filter);
    this.setVisibilityIfChanged(this.registry.routes.layers.default, 'visible');
    this.setVisibilityIfChanged(this.registry.routes.layers.dashed, 'visible');
    this.setVisibilityIfChanged(this.registry.routes.layers.symbols, 'visible');
    this.setVisibilityIfChanged(this.registry.routes.layers.invisible, 'visible');
  }

  private filtersEqual(
    a: mapboxgl.FilterSpecification | null | undefined,
    b: mapboxgl.FilterSpecification
  ): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private hidePaths() {
    if (!this.registry) return;

    if (this.map.getLayer(this.registry.routes.layers.default)) {
      this.map.setLayoutProperty(this.registry.routes.layers.default, 'visibility', 'none');
      this.map.setLayoutProperty(this.registry.routes.layers.dashed, 'visibility', 'none');
      this.map.setLayoutProperty(this.registry.routes.layers.symbols, 'visibility', 'none');
      this.map.setLayoutProperty(
        this.registry.routes.layers.invisible,
        'visibility',
        'none'
      );
    }
  }

  private resetMarkers() {
    if (!this.registry) return;

    this.setFilterIfChanged(this.registry.markers.layers.selected, ['any', false]);
    this.setFilterIfChanged(this.registry.markers.layers.invisible, ['any', true]);
    // Back to every stop: with no filter to partition against, the dots answer to the focus
    // alone, and a stop left out here would be one a focus could never push away.
    this.setFilterIfChanged(this.registry.markers.layers.dim, ['any', true]);

    // Basics
    this.setVisibilityIfChanged(this.registry.markers.layers.unselected, 'none');
    this.setVisibilityIfChanged(this.registry.markers.layers.selected, 'none');
    this.setVisibilityIfChanged(this.registry.markers.layers.invisible, 'visible');
    this.setVisibilityIfChanged(this.registry.markers.layers.default, 'visible');

    if (this.isGroupingEnabled && this.changeGroupingFonction) {
      this.changeGroupingFonction(true, this.markers);
    }
  }

  private resetRoutes() {
    if (!this.registry) return;

    this.setFilterIfChanged(this.registry.routes.layers.default, ['any', true]);
    this.setFilterIfChanged(this.registry.routes.layers.casing, ['any', true]);
    this.setFilterIfChanged(this.registry.routes.layers.dashed, ['any', true]);
    this.setFilterIfChanged(this.registry.routes.layers.symbols, ['any', true]);
    this.setFilterIfChanged(this.registry.routes.layers.invisible, ['any', true]);
    this.showAllLayers();
  }

  private setFilterIfChanged(layerId: string, newFilter: mapboxgl.FilterSpecification) {
    const currentFilter = this.map.getFilter(layerId);
    if (!this.filtersEqual(currentFilter, newFilter)) {
      this.map.setFilter(layerId, newFilter);
    }
  }

  private setVisibilityIfChanged(layerId: string, visibility: 'visible' | 'none') {
    const currentVisibility = this.map.getLayoutProperty(layerId, 'visibility');
    if (currentVisibility !== visibility) {
      this.map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  }

  private showAllLayers() {
    if (!this.registry) return;

    const markers = this.registry.markers.layers;

    // The unlit stops go back under the route lines, so no dot lands on the one route a focus
    // has left visible.
    const under = this.registry.routes.layers.casing;
    for (const layer of [markers.dim, markers.unselected]) {
      this.map.moveLayer(layer, under);
    }

    // Each `moveLayer` below puts its layer on top, so this list *is* the resulting order of
    // the lit stops, bottom to top.
    this.map.moveLayer(markers.unclustered);
    this.map.moveLayer(markers.unclusteredInvisible);
    this.map.moveLayer(markers.clustered);
    this.map.moveLayer(markers.clusteredCount);
    this.map.moveLayer(markers.unclusteredSpiderfy);
    this.map.moveLayer(markers.default);
    this.map.moveLayer(markers.selected);
    this.map.moveLayer(markers.invisible);
    // The numbers read over every pin.
    this.map.moveLayer(markers.sequence);

    this.map.setLayoutProperty(
      this.registry.routes.layers.default,
      'visibility',
      'visible'
    );
    this.map.setLayoutProperty(this.registry.routes.layers.dashed, 'visibility', 'visible');
    this.map.setLayoutProperty(
      this.registry.routes.layers.symbols,
      'visibility',
      'visible'
    );
    this.map.setLayoutProperty(
      this.registry.routes.layers.invisible,
      'visibility',
      'visible'
    );
  }
}
